import React, { useState, useContext } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Menu,
  MenuItem,
  Avatar,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

const SideMenu = () => {
  const { currentUser, logout, activeCompany, setActiveCompany } = useContext(AuthContext);
  const navigate = useNavigate();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [companyMenuAnchorEl, setCompanyMenuAnchorEl] = useState(null);
  const isUserMenuOpen = Boolean(userMenuAnchorEl);
  const isCompanyMenuOpen = Boolean(companyMenuAnchorEl);
  const isAdmin = activeCompany?.CompanyUser?.isAdmin === true;

  const menuItems = [
    { text: 'Tableau de bord', path: '/dashboard', icon: <DashboardIcon /> }
  ];

  menuItems.push(
    { text: 'Mes disponibilités', path: '/availability', icon: <EventAvailableIcon /> },
    { text: 'Mon horaire', path: '/schedule', icon: <ScheduleIcon /> }
  );
  if (isAdmin) {
    menuItems.push(
      { type: 'divider' },
      { text: 'Planning', path: '/planning', icon: <CalendarMonthIcon /> },
      { text: 'Dispo. équipe', path: '/team-availability', icon: <EventRepeatIcon /> },
      { type: 'divider' },
      { text: 'Employés', path: '/employees', icon: <GroupIcon /> },
      { text: 'Entreprise', path: '/company-settings', icon: <SettingsIcon /> }
    );
  }

  const getRoleDisplayName = () => {    
    if (!activeCompany || !activeCompany.CompanyUser) {
      return '';
    }
    if (activeCompany.CompanyUser.isAdmin) {
      return 'Administrateur';
    }else{
      return 'Employé';
    }
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleCompanyMenuOpen = (event) => {
    setCompanyMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setUserMenuAnchorEl(null);
    setCompanyMenuAnchorEl(null);
  };

  const handleAccount = () => {
    navigate('/account');
    handleMenuClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleCompanyChange = (company) => {
    setActiveCompany(company);
    handleMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        {activeCompany && (
          <Box
            onClick={currentUser.Companies.length > 1 ? handleCompanyMenuOpen : null}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: currentUser.Companies.length > 1 ? 'pointer' : 'default',
              width: '100%',
            }}
          >
            <Avatar sx={{ mr: 1.5, bgcolor: 'primary.main', width: 32, height: 32 }}>
              <BusinessIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
              {activeCompany.name}
            </Typography>
            {currentUser.Companies.length > 1 && <ArrowDropDownIcon />}
          </Box>
        )}
      </Toolbar>
      <Menu
        anchorEl={companyMenuAnchorEl}
        open={isCompanyMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ mt: 1 }}
      >
        {currentUser?.Companies.map((company) => (
          <MenuItem key={company.id} onClick={() => handleCompanyChange(company)} selected={activeCompany ? company.id === activeCompany.id : false}>
            {company.name}
          </MenuItem>
        ))}
      </Menu>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item, index) => {
          if (item.type === 'divider') {
            return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
          }
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton component={NavLink} to={item.path}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      {currentUser && (
        <>
          <Box sx={{ p: 2, mt: 'auto' }}>
            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                {currentUser.firstName.charAt(0)}{currentUser.lastName.charAt(0)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >{`${currentUser.firstName} ${currentUser.lastName}`}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >{currentUser.email}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{getRoleDisplayName()}</Typography>
              </Box>
            </Box>
          </Box>
          <Menu
            anchorEl={userMenuAnchorEl}
            open={isUserMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
          >
            <MenuItem onClick={handleAccount}>
              <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
              Mon compte
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Se déconnecter
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default SideMenu;